import { hasRestrictedSegment } from "@homeinstead/shared";
import { getPageType } from "./get-page-type";
import { getRenderedRobots } from "./get-rendered-robots";

/**
 * Properties merged into every Segment `page()` event so SEO compliance can
 * be observed in Mixpanel without a parallel allow-list per page.
 *
 * The canonical Mixpanel violation rule is:
 *
 *   `seoIsRestrictedPath === true && (seoRobotsIndex === true ||
 *   seoRobotsFollow === true)`
 *
 * Surface C (editor-controlled `seoSettings.noIndex` on Site Builder pages)
 * is best monitored by the synthetic crawler — clients don't have a reliable
 * client-side signal of the editor's intended noindex flag.
 */
type SeoPageProperties = {
  /**
   * `false` when the rendered `<meta name="robots">` content includes
   * `noindex`. `null` when no robots meta tag is rendered or the document
   * is unavailable (SSR call site).
   */
  seoRobotsIndex: boolean | null;
  /**
   * `false` when the rendered `<meta name="robots">` content includes
   * `nofollow`. `null` when no robots meta tag is rendered or the document
   * is unavailable (SSR call site).
   */
  seoRobotsFollow: boolean | null;
  /**
   * Raw lowercase robots meta `content` string. `null` when the tag is
   * absent. Useful for debugging the exact rendered directive.
   */
  seoRobotsRaw: string | null;
  /**
   * Result of `hasRestrictedSegment(pathname)`. `true` for any path whose
   * segments include a member of `RESTRICTED_URL_SEGMENTS` (currently
   * `["ppc"]`). Such paths MUST be `noindex, nofollow`.
   */
  seoIsRestrictedPath: boolean;
  /**
   * Stable, low-cardinality surface identifier (e.g.
   * `"ppc-trusted-home-care-local"`, `"site-builder-office-route"`). See
   * `get-page-type.ts` for the taxonomy.
   */
  pageType: string;
};

type GetSeoPagePropertiesArgs = {
  pathname: string | null | undefined;
};

function getSeoPageProperties({
  pathname,
}: GetSeoPagePropertiesArgs): SeoPageProperties {
  const robots = getRenderedRobots();

  return {
    pageType: getPageType(pathname),
    seoIsRestrictedPath: hasRestrictedSegment(pathname),
    seoRobotsFollow: robots.follow,
    seoRobotsIndex: robots.index,
    seoRobotsRaw: robots.raw,
  };
}

export { getSeoPageProperties };
